import * as DialogPrimitive from '@radix-ui/react-dialog';
import { CircleHelpIcon, XIcon } from 'lucide-react';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils.js';

export function Dialog(
  props: ComponentProps<typeof DialogPrimitive.Root>,
): React.ReactNode {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

export function DialogTrigger(
  props: ComponentProps<typeof DialogPrimitive.Trigger>,
): React.ReactNode {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

export function DialogPortal(
  props: ComponentProps<typeof DialogPrimitive.Portal>,
): React.ReactNode {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

export function DialogClose(
  props: ComponentProps<typeof DialogPrimitive.Close>,
): React.ReactNode {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

export function DialogOverlay({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Overlay>): React.ReactNode {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        // Soft dim so the dialog reads as a focused surface against the
        // page. Sonner mounts at z-index 9999 — well above this z-50
        // overlay — so the version-update toast and other global
        // notifications sit on top of the scrim, not behind it.
        'fixed inset-0 z-50 bg-black/40',
        'data-[state=open]:animate-in data-[state=open]:fade-in-0',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        className,
      )}
      {...props}
    />
  );
}

/**
 * The modal shell. Centered, width-capped, and — importantly — **height-capped
 * to the viewport with its own vertical scroll**: a dialog whose content grows
 * past the screen overflowed off both edges with its footer buttons unreachable,
 * because Radix locks the page scroll behind the modal. `max-h` +
 * `overflow-y-auto` keeps the footer reachable at any window height, and
 * `overscroll-contain` stops the scroll chaining out to the locked body.
 *
 * The cap reads `--app-height` (set from `visualViewport` by the app shell) so a
 * mobile keyboard or the Teams Tab chrome shrinks the modal instead of pushing
 * its footer under them; `100dvh` is the fallback.
 *
 * A dialog that lays out its own header/body/footer (most of the `features/*`
 * dialogs: flex column, `p-0`, an inner scroller) still takes over by passing
 * `overflow-hidden`, which wins over these classes through `cn`'s tailwind-merge.
 *
 * Its children get `min-w-0` because a grid item's default `min-width: auto`
 * sizes it to its own min-content. One unbreakable string inside — a mailbox
 * address like `firstname.lastname@some-long-domain.test` — then made the whole
 * panel wider than the modal, and every row on it hung off the right edge while
 * the modal itself kept its width. The dialog never grows to fit its content, so
 * a child must always be allowed to shrink to it.
 */
export function DialogContent({
  className,
  children,
  showCloseButton = true,
  helpHref,
  helpLabel = 'Open the guide for this screen',
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  /** Where the question mark beside the close button goes — a guide section, not a home page. */
  helpHref?: string;
  helpLabel?: string;
}): React.ReactNode {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'fixed left-1/2 top-1/2 z-50 grid w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4',
          'max-h-[calc(var(--app-height,100dvh)-2rem)] overflow-y-auto overscroll-contain',
          '[&>*]:min-w-0',
          'rounded-lg border border-border bg-background p-6 shadow-elev-5',
          // Suppress the browser default outline that appears when Radix
          // programmatically focuses the container on open — see AppDialog's
          // `onOpenAutoFocus = preventDefault` default. Focus still lands on
          // the content for screen readers / Tab traversal, just without the
          // visible ring around the whole modal.
          'outline-none focus:outline-none focus-visible:outline-none',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          'duration-base',
          className,
        )}
        {...props}
      >
        {children}
        {helpHref !== undefined && helpHref.length > 0 && (
          <DialogHelpLink
            href={helpHref}
            label={helpLabel}
            className={cn(
              'absolute top-4',
              showCloseButton ? 'right-11' : 'right-4',
            )}
          />
        )}
        {showCloseButton && (
          <DialogCloseButton className="absolute right-4 top-4" />
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

/** The look the question mark and the close button share, wherever a dialog places them. */
const CORNER_CONTROL_CLASS =
  'rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

/**
 * The question mark that opens the guide for a dialog: a real link, so it can be opened in a new tab
 * or copied like any other. It sits left of the close button, or takes its place when a dialog hides it.
 */
export function DialogHelpLink({
  href,
  label = 'Open the guide for this screen',
  className,
}: {
  href: string;
  label?: string;
  className?: string;
}): React.ReactNode {
  return (
    <a
      data-slot="dialog-help"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      aria-label={label}
      className={cn(CORNER_CONTROL_CLASS, className)}
    >
      <CircleHelpIcon className="size-4" aria-hidden="true" />
    </a>
  );
}

/** The cross that closes a dialog. */
export function DialogCloseButton({
  className,
}: {
  className?: string;
}): React.ReactNode {
  return (
    <DialogPrimitive.Close
      data-slot="dialog-close"
      className={cn(
        CORNER_CONTROL_CLASS,
        'disabled:pointer-events-none',
        className,
      )}
    >
      <XIcon className="size-4" />
      <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
  );
}

export function DialogHeader({
  className,
  ...props
}: ComponentProps<'div'>): React.ReactNode {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        'flex flex-col gap-1.5 text-center sm:text-left',
        className,
      )}
      {...props}
    />
  );
}

export function DialogFooter({
  className,
  ...props
}: ComponentProps<'div'>): React.ReactNode {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  );
}

export function DialogTitle({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Title>): React.ReactNode {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        'font-heading text-lg font-semibold leading-tight tracking-tight',
        className,
      )}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>): React.ReactNode {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}
